import {
    FC,
    useState,
    useRef,
    useMemo,
    useEffect,
    useLayoutEffect,
    useCallback,
} from 'react';
import { useOutsideClick } from '@/hooks/outsudeClick/outsudeClick.hook';
import { lazyRetryWithSuspense } from '@/helpers/lazyComponents';
import { getLocalStorageItem, setLocalStorageItem } from '@/helpers/storage';
import IcoInfoRound from '@/assets/svg/ico_info_round.svg?react';
import IcoTriangle from '@/assets/svg/ico_triangle.svg?react';
import {
    EXTRA_LEFT_SHIFT,
    TOOLTIP_APPEAR_DELAY_MS,
    TOOLTIP_HIDE_DELAY_MS,
} from '@/components/elems/Tooltip/Tooltip.config';
import styles from '@/components/elems/Tooltip/Tooltip.module.scss';
import { TooltipPropsIF } from '@/components/elems/Tooltip/Tooltip.types';
import { STORAGE_KEYS } from '@/constants/storageKeys';

const Overlay = lazyRetryWithSuspense(
    () => import('@/components/elems/Overlay/Overlay.component'),
);

const Tooltip: FC<TooltipPropsIF> = ({
    content,
    containerRef,
    textRef,
    routeKey,
    alignment = 'left',
    extraTopShift = 0,
    paddingLeft = 0,
    isManualOnly = false,
    isInsideSelect = false,
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [shouldRender, setShouldRender] = useState(false);
    const [position, setPosition] = useState({
        left: 0,
        right: 0,
        bottom: 0,
    });
    const [shouldAutoShow, setShouldAutoShow] = useState<boolean>(() => {
        if (isManualOnly) {
            return false;
        }

        if (!routeKey) {
            return true;
        }

        const seenTooltips: Record<string, boolean> =
            getLocalStorageItem(STORAGE_KEYS.SeenTooltipsByRoute) || {};
        return !seenTooltips[routeKey];
    });

    const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    const maxWidth = useMemo(() => {
        const isDvwSupported = CSS.supports('width', '100dvw');
        const viewportUnit = isDvwSupported ? 'dvw' : 'vw';
        return `min(300px, calc(100${viewportUnit} - 2 * ${position.left}px))`;
    }, [position.left]);

    const markAsSeen = useCallback((): void => {
        setShouldAutoShow((previous) => {
            if (previous) {
                if (routeKey && !isManualOnly) {
                    const seenTooltips: Record<string, boolean> =
                        getLocalStorageItem(STORAGE_KEYS.SeenTooltipsByRoute) ||
                        {};
                    seenTooltips[routeKey] = true;
                    setLocalStorageItem(
                        STORAGE_KEYS.SeenTooltipsByRoute,
                        seenTooltips,
                    );
                }
                return false;
            }
            return previous;
        });
    }, [routeKey, isManualOnly]);

    const showTooltip = useCallback((): void => {
        if (isClosing) {
            return;
        }

        if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
            setIsClosing(false);
        }

        if (shouldRender) {
            setIsVisible(true);
            return;
        }

        setShouldRender(true);

        showTimerRef.current = setTimeout(() => {
            setIsVisible(true);
            showTimerRef.current = null;
        }, TOOLTIP_APPEAR_DELAY_MS);
    }, [shouldRender, isClosing]);

    const hideTooltip = useCallback((): void => {
        if (showTimerRef.current) {
            clearTimeout(showTimerRef.current);
            showTimerRef.current = null;
        }

        if (!shouldRender || isClosing) {
            return;
        }

        setIsClosing(true);
        setIsVisible(false);
        markAsSeen();

        hideTimerRef.current = setTimeout(() => {
            setShouldRender(false);
            setIsClosing(false);
            hideTimerRef.current = null;
        }, TOOLTIP_HIDE_DELAY_MS);
    }, [shouldRender, isClosing, markAsSeen]);

    useOutsideClick(tooltipRef, hideTooltip);

    useEffect(() => {
        if (shouldAutoShow) {
            showTooltip();
        }
    }, [shouldAutoShow]);

    useEffect(
        () => () => {
            if (showTimerRef.current) {
                clearTimeout(showTimerRef.current);
            }

            if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current);
            }
        },
        [],
    );

    useLayoutEffect(() => {
        const updatePosition = (): void => {
            if (containerRef?.current && textRef?.current) {
                const containerRect =
                    containerRef.current.getBoundingClientRect();
                const textRect = textRef.current.getBoundingClientRect();
                setPosition({
                    left: containerRect.left,
                    right: textRect.right,
                    bottom: containerRect.bottom,
                });
            }
        };

        updatePosition();
        const rafId = requestAnimationFrame(updatePosition);

        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);

        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [containerRef, textRef]);

    return (
        <>
            {shouldRender && isVisible && (
                <Overlay className="flc">
                    <div
                        ref={tooltipRef}
                        className={styles.tooltipContent}
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                        role="presentation"
                        style={{
                            top: `${position.bottom + extraTopShift}px`,
                            ...(alignment === 'center'
                                ? { margin: '0 auto' }
                                : { left: `${position.left}px` }),
                            maxWidth: maxWidth,
                        }}
                    >
                        {content}
                        <IcoTriangle className={styles.arrow} />
                    </div>
                </Overlay>
            )}

            <div
                className={`
                    ${styles.iconTooltipWrapper}
                    ${shouldAutoShow ? '' : styles.visible}
                    ${isInsideSelect ? '' : styles.verticallyCentered}
                `}
                style={{
                    left: `${position.right - position.left + (isInsideSelect ? paddingLeft : 0) + EXTRA_LEFT_SHIFT}px`,
                }}
            >
                <IcoInfoRound
                    className={styles.iconTooltip}
                    onClick={(event) => {
                        event.stopPropagation();
                        showTooltip();
                    }}
                />
            </div>
        </>
    );
};

export default Tooltip;
